
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import useTranslation from "next-translate/useTranslation";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import BaseLayout from "@components/BaseLayout";
import TranslatedHtml from "@components/common/TranslatedHtml";
import { Routes } from "@services/routes";
import { BASE_URL, LEGAL_BASIS } from "config";
/**
 * This page shows the data protection information
 *
 * for costumization especially the following elements must be replaced
 * dataProtection.mandatoryInformation.responsableInstitution
 * dataProtection.mandatoryInformation.dataProtectionOfficer
 *
 *
 * "legal basis" (Rechtsgrundlage) is the basic of which the data protection policy is based.
 * by default is is defined as
 * "Art. 6 Abs. 1 S. 1. lit. f DSGVO bzw. Art. 6 Abs. 1 S. 1 lit. b DSGVO"
 * in the translations.
 *
 * The attourney says:
 * "Für Kommunen oder andere öffentliche Träger gelten jeweils landesbezogene Rechtsgrundlagen:
 * Rechtsgrundlage aus dem jeweiligen Landesdatenschutzgesetz für die Zulässigkeit der Verarbeitung personenbezogener Daten für öffentliche Stellen, z.B. § 3 NDSG; § 5 BbgDSG; §§ 4, 12 ff. SächsDSG."
 *
 * For customization of the content change /locales/custom-locales/de/data-protection.json
 *
 * @see /doc/datenschutz.md
 *
 * @todo add more legal basis for federal states in the translation file! https://futureprojects.atlassian.net/jira/software/projects/FCP/boards/1?selectedIssue=FCP-784
 * @todo store and load the legal basis information from the process/program instead from the env/config: https://futureprojects.atlassian.net/jira/software/projects/FCP/boards/1?selectedIssue=FCP-785
 */
const DataProtectionPage: React.FC = () => {
    // use special locales file, because of large data amount
    const { t } = useTranslation("data-protection");
    const legalBasis = t("dataProtection.legalBasis." + LEGAL_BASIS);
    const dataProcessingSections: string[] = [
        "platformVisit",
        "registration",
        "projectPlanner",
        "contact",
        "videoConference",
        "protocolling",
        "errorAnalysis",
        "newsletter"
    ];
    return <BaseLayout pageTitle={t("dataProtection.title")}>
    <Card>
      <CardHeader>
        <h1>{t("dataProtection.title")}</h1>
      </CardHeader>
      <CardBody className="title-counter">
        <Row>
          <Col className="text-justify">
            {/* terms and definitions */}
            <h1><TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.title"/></h1>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.intro"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.personalData"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.cookies"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.processing"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.legalBasis"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.receiver"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.responsibleParty"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.consent"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.thirdCountry"/>
            <TranslatedHtml content="data-protection:dataProtection.termsAndDefinitions.profiling"/>
            {/* mandatory information */}
            <h1><TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.title"/></h1>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.intro" params={{ url: BASE_URL }}/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.responsableInstitution"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.dataProtectionOfficer"/>
            {/* your rights */}
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.title"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.intro" params={{ imprint: Routes.Imprint }}/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfInformation"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfCorrection"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfProcessingLimitation"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfDeletion"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfBriefing"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightOfDataTransfer"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightToProtest"/>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.yourRights.rightToComplain"/>
            {/* data processing */}
            <h2>{t("dataProtection.mandatoryInformation.dataProcessing.title")}</h2>
            <p>{t("dataProtection.mandatoryInformation.dataProcessing.intro")}</p>
            {dataProcessingSections.map(section => {
            return <div key={section}>
                <h3>{t("dataProtection.mandatoryInformation.dataProcessing." + section + ".title")}</h3>
                <h4>{t("dataProtection.mandatoryInformation.dataProcessing.dataCategories")}</h4>
                <TranslatedHtml content={"data-protection:dataProtection.mandatoryInformation.dataProcessing." + section + ".dataCategories"}/>
                <h4>{t("dataProtection.mandatoryInformation.dataProcessing.purpose")}</h4>
                <p>{t("dataProtection.mandatoryInformation.dataProcessing." + section + ".purpose")}</p>
                <h4>{t("dataProtection.mandatoryInformation.dataProcessing.storageTime")}</h4>
                <p>{t("dataProtection.mandatoryInformation.dataProcessing." + section + ".storageTime")}</p>
                <h4>{t("dataProtection.mandatoryInformation.dataProcessing.receiver")}</h4>
                <TranslatedHtml content={"data-protection:dataProtection.mandatoryInformation.dataProcessing." + section + ".receiver"}/>
                <h4>{t("dataProtection.mandatoryInformation.dataProcessing.legalBasis")}</h4>
                <p>{t("dataProtection.mandatoryInformation.dataProcessing." + section + ".legalBasis", { legalBasis })}</p>
              </div>;
        })}
            {/* updates */}
            <h2><TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.update.title"/></h2>
            <TranslatedHtml content="data-protection:dataProtection.mandatoryInformation.update.intro"/>

          </Col>
        </Row>
      </CardBody>
    </Card>
  </BaseLayout>;
};
export default DataProtectionPage;

    async function __Next_Translate__getStaticProps__18c4f51713d__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/data-protection',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__18c4f51713d__ as getStaticProps }
  